import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VaRCalculations = ({ data, historicalData }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1d');
  const [selectedConfidence, setSelectedConfidence] = useState('95');

  const timeframes = [
    { value: '1d', label: '1 Day' },
    { value: '1w', label: '1 Week' },
    { value: '1m', label: '1 Month' }
  ];

  const confidenceLevels = [
    { value: '90', label: '90%' },
    { value: '95', label: '95%' },
    { value: '99', label: '99%' }
  ];

  const getCurrentVaR = () => {
    const key = `var_${selectedTimeframe}_${selectedConfidence}`;
    return data?.[key] || data?.var_1d_95;
  };

  const getVaRStatus = (varValue) => {
    const portfolioValue = 50000000; // ₹5 Crores
    const varPercentage = Math.abs(varValue / portfolioValue) * 100;
    
    if (varPercentage > 5) return { status: 'critical', message: 'High Risk' };
    if (varPercentage > 3) return { status: 'warning', message: 'Moderate Risk' };
    return { status: 'good', message: 'Low Risk' };
  };

  const currentVaR = getCurrentVaR();
  const varStatus = getVaRStatus(currentVaR);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-popover-foreground">{label}</p>
          <p className="text-sm text-muted-foreground">
            VaR: ₹{Math.abs(payload?.[0]?.value)?.toLocaleString('en-IN')}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Value at Risk (VaR) Analysis</h3>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
          varStatus?.status === 'critical' ? 'bg-error/10 text-error' :
          varStatus?.status === 'warning'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
        }`}>
          <Icon 
            name={varStatus?.status === 'critical' ? 'AlertTriangle' : varStatus?.status === 'warning' ? 'AlertCircle' : 'Shield'} 
            size={16} 
          />
          <span>{varStatus?.message}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <div className="lg:col-span-1 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Time Horizon</label>
            <div className="space-y-1">
              {timeframes?.map((timeframe) => (
                <button
                  key={timeframe?.value}
                  onClick={() => setSelectedTimeframe(timeframe?.value)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-smooth ${
                    selectedTimeframe === timeframe?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  {timeframe?.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Confidence Level</label>
            <div className="space-y-1">
              {confidenceLevels?.map((level) => (
                <button
                  key={level?.value}
                  onClick={() => setSelectedConfidence(level?.value)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-smooth ${
                    selectedConfidence === level?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  {level?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingDown" size={16} className="text-error" />
                <span className="text-sm font-medium text-foreground">Current VaR</span>
              </div>
              <div className="text-2xl font-bold text-error">
                ₹{Math.abs(currentVaR)?.toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-muted-foreground">
                {selectedConfidence}% confidence, {timeframes?.find(t => t?.value === selectedTimeframe)?.label}
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Percent" size={16} className="text-warning" />
                <span className="text-sm font-medium text-foreground">Portfolio %</span>
              </div>
              <div className="text-2xl font-bold text-warning">
                {((Math.abs(currentVaR) / 50000000) * 100)?.toFixed(2)}%
              </div>
              <div className="text-xs text-muted-foreground">
                Of total portfolio value
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Calendar" size={16} className="text-accent" />
                <span className="text-sm font-medium text-foreground">Expected Shortfall</span>
              </div>
              <div className="text-2xl font-bold text-accent">
                ₹{Math.abs(currentVaR * 1.3)?.toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-muted-foreground">
                Conditional VaR (CVaR)
              </div>
            </div>
          </div>

          <div className="h-64">
            <h4 className="text-sm font-semibold text-foreground mb-3">Historical VaR Trend</h4>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historicalData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  tickFormatter={(value) => `₹${Math.abs(value / 1000)}K`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="var" 
                  stroke="var(--color-error)" 
                  strokeWidth={2}
                  dot={{ fill: 'var(--color-error)', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="border-t border-border pt-4">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Method:</span>
            <span className="ml-2 font-medium text-foreground">Historical Simulation</span>
          </div>
          <div>
            <span className="text-muted-foreground">Sample Size:</span>
            <span className="ml-2 font-medium text-foreground">252 days</span>
          </div>
          <div>
            <span className="text-muted-foreground">Last Updated:</span>
            <span className="ml-2 font-medium text-foreground">2 min ago</span>
          </div>
          <div>
            <span className="text-muted-foreground">Backtesting:</span>
            <span className="ml-2 font-medium text-success">98.2% accuracy</span>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" iconName="Download" iconSize={14}>
              Export
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaRCalculations;